import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppState } from '../context/AppContext'
import {
  IPdfExportParams,
  PdfExportScope,
  PdfExportType,
} from '../common/models/PdfExport'
import { ICompanyMember } from '../common/models/Company'
import type { ICollectionBulkOptions } from '../utils/pdf/renderCollectionBulk'
import { formatDate } from '../utils/dateHelper'
import {
  isPdfProbeEnabled,
  logPdfTrace,
  pdfTraceText,
} from '../utils/pdf/pdfDiagnostics'

interface IExportArgs {
  exportType: PdfExportType
  params: IPdfExportParams
}

interface IBulkScopeArgs {
  exportType: PdfExportType
  scope: PdfExportScope
  scopeId?: string
}

type ProgressFn = (done: number, total: number) => void
type TranslateFn = (key: string) => string

// Triggers a browser download of a rendered blob (a zip or CSV).
const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

// User-facing download name: strip only filesystem-reserved chars, keep accents
// (safeSegment would drop diacritics, mangling Dutch titles like "Financiën").
const safeFilename = (name: string) =>
  name
    .replace(/[\\/:*?"<>|]/g, '')
    .trim()
    .slice(0, 120) || 'export'

// Singular doc-type words, reusing the keys the PDF documents already title with.
const DOC_TYPE_KEY: Partial<Record<PdfExportType, string>> = {
  [PdfExportType.JOB_PROFILE]: 'jobProfilePdf.title',
  [PdfExportType.EVALUATION_FORM]: 'evaluationFormPdf.title',
  [PdfExportType.TERM_RESULTS]: 'termResultsPdf.title',
}

// "{level} - {type} - {date}.pdf" — collections carry their own title, no type word.
const pdfDownloadName = (
  exportType: PdfExportType,
  title: string,
  t: TranslateFn
) => {
  const typeKey = DOC_TYPE_KEY[exportType]
  const parts = [safeFilename(title)]
  if (typeKey) parts.push(t(typeKey))
  parts.push(formatDate(new Date()))
  return `${parts.join(' - ')}.pdf`
}

// Surface render failures in the opened tab instead of slamming it shut, so the
// failure is visible (and we keep the gesture-opened tab). The diagnostic trace is
// printed under the message so it can be copied straight from the failed tab.
const showErrorInTab = (
  win: Window | null,
  heading: string,
  error: unknown
) => {
  const message = error instanceof Error ? error.message : String(error)
  if (win && !win.closed) {
    win.document.body.innerHTML =
      '<div style="font-family:sans-serif;padding:24px;color:#b00020">' +
      `<h3>${heading}</h3>` +
      '<pre style="white-space:pre-wrap">' +
      message +
      '</pre>' +
      '<pre style="white-space:pre-wrap;color:#555;font-size:11px">' +
      pdfTraceText() +
      '</pre></div>'
  }
}

/**
 * Client-side export. PDFs are rendered in the browser with @react-pdf (lazy-loaded)
 * and opened in a new tab; CSVs are built off the company and downloaded. Nothing is
 * stored server-side and nothing is persisted — the export is created and handed off.
 */
export const usePdfExport = () => {
  const { company, userAuthToken } = useAppState()
  const { t } = useTranslation()
  const [isExporting, setIsExporting] = useState(false)

  const render = useCallback(
    async (
      exportType: PdfExportType,
      params: IPdfExportParams,
      win: Window | null
    ): Promise<string> => {
      if (!company) throw new Error('NO_COMPANY')
      // Lazy import keeps the heavy @react-pdf bundle out of the main chunk.
      const { renderPdfBlob } = await import('../utils/pdf/registry')
      // The token lets the collection template fetch its (visibility-filtered)
      // data; the other templates ignore it.
      const rendering = renderPdfBlob(
        exportType,
        params,
        company,
        t,
        userAuthToken
      )
      // The probe deliberately renders the document many times over, so the normal
      // timeout would always trip it.
      const timeout = isPdfProbeEnabled()
        ? rendering
        : new Promise((_, reject) =>
            setTimeout(() => reject(new Error('PDF render timed out')), 20000)
          )
      const { blob, title } = (await Promise.race([rendering, timeout])) as {
        blob: Blob
        title: string
      }
      if (win && !win.closed) {
        const url = URL.createObjectURL(blob)
        win.location.href = url
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      }
      // A blob-URL tab save would be named after the random UUID, so always drop a
      // properly-named copy in Downloads (this also covers the popup-blocked case).
      downloadBlob(blob, pdfDownloadName(exportType, title, t))
      return title
    },
    [company, t, userAuthToken]
  )

  // Open the tab synchronously (within the click gesture) so it isn't blocked,
  // then point it at the blob once rendering finishes.
  const openPlaceholderTab = useCallback((): Window | null => {
    const win = window.open('', '_blank')
    if (win) {
      win.document.write(
        `<title>${t('fileSettings.generating')}</title>` +
          `<body style="font-family:sans-serif;padding:24px;color:#555">` +
          `${t('fileSettings.generating')}</body>`
      )
    }
    return win
  }, [t])

  // A single profile export is a pure render-and-open action: nothing is stored.
  const exportPdf = useCallback(
    async ({ exportType, params }: IExportArgs) => {
      const win = openPlaceholderTab()
      setIsExporting(true)
      try {
        await render(exportType, params, win)
      } catch (error) {
        console.error('[PDF] export failed', error)
        showErrorInTab(win, t('fileSettings.reExportFailed'), error)
        throw error
      } finally {
        // Printed on success too: comparing a failing company against a working one
        // needs both traces.
        logPdfTrace()
        setIsExporting(false)
      }
    },
    [openPlaceholderTab, render, t]
  )

  // CSV twin of exportPdf: lazy-loads the CSV render (keeps it out of the main
  // bundle, like the PDF path) and downloads the file. A CSV isn't viewable in a
  // tab, so it goes straight to download.
  const exportCsv = useCallback(
    async ({ params }: { params: IPdfExportParams }): Promise<void> => {
      if (!company) throw new Error('NO_COMPANY')
      setIsExporting(true)
      try {
        const { renderJobProfileCsvBlob } = await import(
          '../utils/csv/renderJobProfileCsv'
        )
        const { blob, title } = await renderJobProfileCsvBlob(
          params,
          company,
          t
        )
        downloadBlob(blob, `${title}.csv`)
      } finally {
        setIsExporting(false)
      }
    },
    [company, t]
  )

  // DOCX twin of exportCsv: lazy-loads the Word render and downloads the file.
  // Like a CSV it isn't viewable in a tab, so it goes straight to download.
  const exportDocx = useCallback(
    async ({ params }: { params: IPdfExportParams }): Promise<void> => {
      if (!company) throw new Error('NO_COMPANY')
      setIsExporting(true)
      try {
        const { renderJobProfileDocxBlob } = await import(
          '../utils/docx/renderJobProfileDocx'
        )
        const { blob, title } = await renderJobProfileDocxBlob(
          params,
          company,
          t
        )
        downloadBlob(blob, `${title}.docx`)
      } finally {
        setIsExporting(false)
      }
    },
    [company, t]
  )

  // Scope-aware job-profile Word export: a single .docx for one level, or a
  // folder-structured zip of per-profile .docx files for a growth path / domain /
  // company. Word twin of exportJobProfileCsv.
  const exportJobProfileDocx = useCallback(
    async ({
      scope,
      scopeId,
    }: {
      scope: PdfExportScope
      scopeId?: string
    }): Promise<void> => {
      if (!company) throw new Error('NO_COMPANY')
      setIsExporting(true)
      try {
        if (scope === PdfExportScope.LEVEL) {
          const { renderJobProfileDocxBlob } = await import(
            '../utils/docx/renderJobProfileDocx'
          )
          const { blob, title } = await renderJobProfileDocxBlob(
            { scope, jobLevelId: scopeId },
            company,
            t
          )
          downloadBlob(blob, `${title}.docx`)
          return
        }
        const { renderJobProfileDocxZipBlob } = await import(
          '../utils/docx/renderJobProfileDocx'
        )
        const { blob: zipBlob } = await renderJobProfileDocxZipBlob(
          scope,
          scopeId,
          company,
          t
        )
        const name = `${company.name} ${t(
          'fileSettings.pdfSettings.jobProfiles'
        )} ${formatDate(new Date())}`
        downloadBlob(zipBlob, `${name}.zip`)
      } finally {
        setIsExporting(false)
      }
    },
    [company, t]
  )

  // Scope-aware job-profile CSV: a single spreadsheet for one level, or a
  // folder-structured zip of per-profile CSVs for a growth path / domain / company.
  const exportJobProfileCsv = useCallback(
    async ({
      scope,
      scopeId,
    }: {
      scope: PdfExportScope
      scopeId?: string
    }): Promise<void> => {
      if (!company) throw new Error('NO_COMPANY')
      setIsExporting(true)
      try {
        if (scope === PdfExportScope.LEVEL) {
          const { renderJobProfileCsvBlob } = await import(
            '../utils/csv/renderJobProfileCsv'
          )
          const { blob, title } = await renderJobProfileCsvBlob(
            { scope, jobLevelId: scopeId },
            company,
            t
          )
          downloadBlob(blob, `${title}.csv`)
          return
        }
        const { renderJobProfileCsvZipBlob } = await import(
          '../utils/csv/renderJobProfileCsv'
        )
        const { blob: zipBlob } = await renderJobProfileCsvZipBlob(
          scope,
          scopeId,
          company,
          t
        )
        const name = `${company.name} ${t(
          'fileSettings.pdfSettings.jobProfiles'
        )} ${formatDate(new Date())}`
        downloadBlob(zipBlob, `${name}.zip`)
      } finally {
        setIsExporting(false)
      }
    },
    [company, t]
  )

  // Bulk CSV twin of exportBulk: builds one flat spreadsheet (a row per job level)
  // for the scope and downloads it. The table is read straight off the company so
  // there's no async render and nothing is persisted.
  const exportBulkCsv = useCallback(
    async ({
      scope,
      scopeId,
    }: {
      scope: PdfExportScope
      scopeId?: string
    }): Promise<void> => {
      if (!company) throw new Error('NO_COMPANY')
      setIsExporting(true)
      try {
        const { renderJobsTableCsvBlob } = await import(
          '../utils/csv/renderJobsTableCsv'
        )
        const { blob, title } = renderJobsTableCsvBlob(
          scope,
          scopeId,
          company,
          t
        )
        downloadBlob(blob, `${title}.csv`)
      } finally {
        setIsExporting(false)
      }
    },
    [company, t]
  )

  // Skills CSV twin of exportBulkCsv: builds a skills × job-level matrix for the
  // scope and downloads it. Like the jobs table it's read straight off the
  // company — no async render, nothing persisted.
  const exportSkillsCsv = useCallback(
    async ({
      scope,
      scopeId,
    }: {
      scope: PdfExportScope
      scopeId?: string
    }): Promise<void> => {
      if (!company) throw new Error('NO_COMPANY')
      setIsExporting(true)
      try {
        const { renderSkillsTableCsvBlob } = await import(
          '../utils/csv/renderSkillsTableCsv'
        )
        const { blob, title } = renderSkillsTableCsvBlob(
          scope,
          scopeId,
          company,
          t
        )
        downloadBlob(blob, `${title}.csv`)
      } finally {
        setIsExporting(false)
      }
    },
    [company, t]
  )

  // Renders a bulk scope's combined PDF and points the opened tab at it. Unlike a
  // single export there's no tight render timeout — a combined doc can take longer.
  const renderBulkInto = useCallback(
    async (
      win: Window | null,
      exportType: PdfExportType,
      scope: PdfExportScope,
      scopeId: string | undefined,
      onProgress?: ProgressFn,
      signal?: AbortSignal
    ): Promise<string> => {
      if (!company) throw new Error('NO_COMPANY')
      const { renderPdfBulk } = await import('../utils/pdf/registry')
      const { combinedBlob, title } = await renderPdfBulk(
        exportType,
        scope,
        scopeId,
        company,
        t,
        { combined: true, zip: false },
        onProgress || (() => undefined),
        signal
      )
      if (!combinedBlob) throw new Error('NO_PROFILES')
      if (win && !win.closed) {
        const url = URL.createObjectURL(combinedBlob)
        win.location.href = url
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      }
      // Same as the single export: keep the preview tab but hand off a named copy.
      downloadBlob(combinedBlob, pdfDownloadName(exportType, title, t))
      return title
    },
    [company, t]
  )

  // Bulk PDF export: render the scope's combined PDF client-side and open it in the
  // viewer. Nothing is stored; `onProgress` drives the modal's counter.
  const exportBulk = useCallback(
    async (
      { exportType, scope, scopeId }: IBulkScopeArgs,
      onProgress?: ProgressFn,
      signal?: AbortSignal
    ): Promise<void> => {
      const win = openPlaceholderTab()
      setIsExporting(true)
      try {
        await renderBulkInto(
          win,
          exportType,
          scope,
          scopeId,
          onProgress,
          signal
        )
      } catch (error) {
        console.error('[PDF] bulk export failed', error)
        showErrorInTab(win, t('fileSettings.reExportFailed'), error)
        throw error
      } finally {
        logPdfTrace()
        setIsExporting(false)
      }
    },
    [openPlaceholderTab, renderBulkInto, t]
  )

  // On-demand loose-PDF zip for a bulk scope, downloaded as
  // "{company} {Job profiles} {dd-mm-yyyy}.zip". Offered from the modal's done state.
  const downloadBulkPdfZip = useCallback(
    async (
      { exportType, scope, scopeId }: IBulkScopeArgs,
      onProgress?: ProgressFn,
      signal?: AbortSignal
    ): Promise<void> => {
      if (!company || scope === PdfExportScope.LEVEL) return
      setIsExporting(true)
      try {
        const { renderPdfBulk } = await import('../utils/pdf/registry')
        const { zipBlob } = await renderPdfBulk(
          exportType,
          scope,
          scopeId,
          company,
          t,
          { combined: false, zip: true },
          onProgress || (() => undefined),
          signal
        )
        if (!zipBlob) return
        const docWord =
          exportType === PdfExportType.EVALUATION_FORM
            ? t('fileSettings.pdfSettings.evaluationForm')
            : t('fileSettings.pdfSettings.jobProfiles')
        const name = `${company.name} ${docWord} ${formatDate(new Date())}`
        downloadBlob(zipBlob, `${name}.zip`)
      } finally {
        setIsExporting(false)
      }
    },
    [company, t]
  )

  // Bulk collection export: render every selected member × term collection
  // client-side and download them as one folder-structured zip. Heavier than the
  // job-profile bulk (each collection is fetched + rendered), so it always streams
  // to a zip — never a combined document — and reports progress. Nothing is stored.
  const exportCollectionBulk = useCallback(
    async (
      {
        members,
        options,
      }: {
        members: ICompanyMember[]
        options: ICollectionBulkOptions
      },
      onProgress?: ProgressFn,
      signal?: AbortSignal
    ): Promise<{ rendered: number; failed: number }> => {
      if (!company) throw new Error('NO_COMPANY')
      if (!userAuthToken) throw new Error('NO_TOKEN')
      setIsExporting(true)
      try {
        const { renderCollectionBulkZip } = await import(
          '../utils/pdf/renderCollectionBulk'
        )
        const { zipBlob, rendered, failed } = await renderCollectionBulkZip(
          members,
          company,
          t,
          userAuthToken,
          options,
          onProgress || (() => undefined),
          signal
        )
        const name = `${company.name} ${t(
          'collectionBulkExport.documentName'
        )} ${formatDate(new Date())}`
        downloadBlob(zipBlob, `${name}.zip`)
        return { rendered, failed }
      } finally {
        setIsExporting(false)
      }
    },
    [company, t, userAuthToken]
  )

  return {
    exportPdf,
    exportCsv,
    exportDocx,
    exportBulk,
    exportBulkCsv,
    exportSkillsCsv,
    exportJobProfileCsv,
    exportJobProfileDocx,
    downloadBulkPdfZip,
    exportCollectionBulk,
    isExporting,
  }
}
